import React from "react";
import ReactDOM from "react-dom";
import { I18n } from "../../../../i18n/i18n";
import { ScenarioElement } from "../../../../model/playerConfiguration/ScenarioElement";
import { ScenarioPath } from "../../../../model/scenarioPath/ScenarioPath";
import { TooltipMetadata } from "../../../../uiModel/tooltip/TooltipMetadata";
import { TimeFormatter } from "../../../../utils/TimeFormatter";
import { PartComponent } from "../common/PartComponent";
import { SliderComponent } from "../common/SliderComponent";

import "@styles/c-desktopSeekbar.scss";

interface Props {
  i18n: I18n;
  scenarioPath: ScenarioPath;
  totalTime: number;
  ellapsedTime: number;
  bufferedTime: number;
  onSeekTo: (time: number) => void;
  onJumpToScenarioElement: (scenarioElement: ScenarioElement, progress: number) => void;
  onHideTooltip: () => void;
  onShowTooltip: (event: TooltipMetadata) => void;
  onSeekStart: () => void;
  onSeekEnd: () => void;
}

interface State {}

export class DesktopSeekbarComponent extends React.Component<Props, State> {
  private static TOOLTIP_DISTANCE = 6;

  private slider: SliderComponent;
  private sliderHtml: HTMLElement;

  render() {
    const { scenarioPath, totalTime, ellapsedTime, bufferedTime, onJumpToScenarioElement, onHideTooltip, onSeekStart, onSeekEnd } =
      this.props;

    const progress = ellapsedTime / totalTime;
    const bufferingProgress = bufferedTime / totalTime;
    return (
      <div className="c-desktopSeekbar">
        {scenarioPath.displayedPastParts.map((part, index) => (
          <PartComponent
            key={part.id}
            part={part}
            onPartClicked={(scenarioElement, jumpProgress) => onJumpToScenarioElement(scenarioElement, jumpProgress)}
            onMouseMove={(event) => this.handlePartMouseMove(event, index)}
            onMouseLeave={() => onHideTooltip()}
          />
        ))}
        <SliderComponent
          className="c-desktopSeekbar__slider"
          style={{
            flexBasis: `${scenarioPath.currentPart.size}%`,
          }}
          ref={(element) => (this.slider = element)}
          value={progress}
          secondaryValue={bufferingProgress}
          onSliderInteraction={(value, isChanging) => this.handleSliderInteraction(value, isChanging)}
          onSliderInteractionEnded={(wasChanging) => {
            onHideTooltip();
            if (wasChanging) {
              onSeekEnd();
            }
          }}
          onChangingSliderInteractionStarted={onSeekStart}
        />

        {scenarioPath.displayedFutureParts.map((part) => (
          <div
            className="c-desktopSeekbar__partContainer"
            style={{
              flexBasis: `${part.size}%`,
            }}
            key={part.id}
          >
            <div className="c-desktopSeekbar__part" />
          </div>
        ))}
      </div>
    );
  }

  componentDidMount() {
    this.sliderHtml = ReactDOM.findDOMNode(this.slider) as HTMLElement;
  }

  private handlePartMouseMove(event: React.MouseEvent, partIndex: number) {
    const { i18n, onShowTooltip } = this.props;
    const partBoundingBox = (event.target as HTMLElement).getBoundingClientRect();
    onShowTooltip({
      x: event.clientX,
      y: partBoundingBox.top - DesktopSeekbarComponent.TOOLTIP_DISTANCE,
      target: "seekbarPart",
      text: `${i18n.part} ${partIndex + 1}`,
    });
  }

  private handleSliderInteraction(value: number, isChanging: boolean) {
    const { onShowTooltip, totalTime } = this.props;
    if (isChanging) {
      this.props.onSeekTo(value * totalTime);
    }

    const sliderBoundingRect = this.sliderHtml.getBoundingClientRect();

    const sliderTooltipX = sliderBoundingRect.left + sliderBoundingRect.width * value;
    const sliderTooltipY = sliderBoundingRect.top - DesktopSeekbarComponent.TOOLTIP_DISTANCE;

    onShowTooltip({
      x: sliderTooltipX,
      y: sliderTooltipY,
      target: "seekbarSlider",
      text: TimeFormatter.format(value * totalTime),
    });
  }
}
